import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import './DriversSegmentation.css';
import { 
  getDriverSegmentationData, 
  storeDriverSegmentationData,
  isDriverSegmentationCacheValid
} from '../../utils/dataCache';
import { API_BASE_URL } from '../../api/apiClient';

const DriversSegmentation = ({ vehicleId, disableNavigation = false }) => {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maxCount, setMaxCount] = useState(0);
  const [totalDrivers, setTotalDrivers] = useState(0);
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const chartRef = useRef(null);
  const navigate = useNavigate();

  // Function to process and set data
  const processData = (data) => {
    // Calculate total number of drivers
    const total = Object.values(data).reduce((sum, count) => sum + count, 0);
    setTotalDrivers(total);
    
    // Transform the data to match component requirements
    const formattedData = [
      { level: 'Excellent', count: data.Excellent || 0, className: 'safe', icon: '' },
      { level: 'Good', count: data.Good || 0, className: 'low-risk', icon: '' },
      { level: 'Average', count: data.Average || 0, className: 'mid-risk', icon: '' },
      { level: 'Poor', count: data.Poor || 0, className: 'high-risk', icon: '' }
    ];
    
    // Calculate the maximum count for scaling
    const max = Math.max(...formattedData.map(item => item.count));
    setMaxCount(max === 0 ? 1 : max); // Avoid division by zero
    
    setSegments(formattedData);
    
    // Store in the centralized cache
    storeDriverSegmentationData(data, vehicleId);
  };

  useEffect(() => {
    const fetchSegmentationData = async () => {
      setLoading(true);
      
      // Check if we have valid cached data for the current vehicleId from centralized cache
      if (isDriverSegmentationCacheValid(vehicleId)) {
        // Use cached data
        const cachedData = getDriverSegmentationData(vehicleId);
        processData(cachedData);
        setLoading(false);
        
        // Still update in background for next time
        refreshDataInBackground();
      } else {
        // Fetch new data
        try {
          await fetchFreshData();
        } catch (error) {
          console.error('Error fetching driver segmentation:', error);
          setLoading(false);
        }
      }
    };
    
    const fetchFreshData = async () => {
      try {
        let url = `${API_BASE_URL}/api/driver-score-summary`;
        const params = new URLSearchParams();
        
        if (vehicleId) {
          params.append('vehicleId', vehicleId);
        }
        
        // Add cache-busting parameter
        params.append('_t', Date.now());
        
        if (params.toString()) {
          url += `?${params.toString()}`;
        }
        
        const response = await fetch(url);
        
        if (!response.ok) throw new Error('Failed to fetch driver segmentation data');
        const data = await response.json();
        
        // Process and cache the data
        processData(data);
      } catch (error) {
        console.error('Error fetching fresh data:', error);
        throw error;
      } finally {
        setLoading(false);
      }
    };
    
    // Update in background without showing loading state
    const refreshDataInBackground = async () => {
      try {
        await fetchFreshData();
      } catch (error) {
        console.error('Background refresh error:', error);
        // Don't update UI on background refresh errors
      }
    };

    fetchSegmentationData();

    // Add animation delay for sequence animation
    const timer = setTimeout(() => {
      const bars = document.querySelectorAll('.risk-bar');
      bars.forEach((bar) => {
        bar.style.width = bar.getAttribute('data-width');
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [vehicleId]);

  const getPercentage = (count) => {
    if (totalDrivers === 0) return '0%';
    return `${Math.round((count / totalDrivers) * 100)}%`;
  };

  const handleSegmentHover = (segment, e) => {
    // Get position for tooltip
    const rect = e.currentTarget.getBoundingClientRect();
    const chartRect = chartRef.current.getBoundingClientRect();
    
    setTooltipPosition({
      x: rect.left - chartRect.left + rect.width / 2,
      y: rect.top - chartRect.top - 5
    });
    
    setHoveredSegment(segment);
  };

  const handleSegmentLeave = () => {
    setHoveredSegment(null);
  };

  const handleClick = () => {
    // Only navigate if navigation is not disabled
    if (!disableNavigation) {
      navigate('/driver-score-category');
    }
  };

  if (loading) {
    return (
      <div className="segmentation modern" ref={chartRef}>
        <div className="chart-header">
          <h3 className="chart-title">Drivers Segmentation</h3>
          <div className="chart-actions">
            <div className="total-drivers">
              <span className="total-count">Loading...</span>
            </div>
          </div>
        </div>
        <div className="chart-grid">
          {[1, 2, 3, 4].map(index => (
            <div key={index} className="skeleton-bar">
              <div className="skeleton-label"></div>
              <div className="skeleton-bar-container">
                <div className="skeleton-progress"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`segmentation modern ${disableNavigation ? 'no-navigation' : ''}`} 
      ref={chartRef} 
      onClick={disableNavigation ? undefined : handleClick}
      style={disableNavigation ? {} : { cursor: 'pointer' }}
    >
      <div className="chart-header">
        <h3 className="chart-title">Drivers Segmentation</h3>
        <div className="chart-actions">
          <div className="total-drivers">
            <span className="total-count">{totalDrivers} drivers</span>
          </div>
        </div>
      </div>

      <div className="segmentation-legend">
        <div className="legend-item">
          <span className="legend-label">Driver Score Categories</span>
        </div>
        <div className="legend-item">
          <span className="legend-label">Count</span>
        </div>
        <div className="legend-item">
          <span className="legend-label">Distribution</span>
        </div>
      </div>

      <div className="chart-grid">
        {segments.map((segment) => (
          <div 
            key={segment.level} 
            className="segmentation-item"
            onMouseEnter={(e) => handleSegmentHover(segment, e)}
            onMouseLeave={handleSegmentLeave}
          >
            <div className={`risk-level ${segment.className}`}>
              <span className="segment-icon">{segment.icon}</span>
              <span className="segment-name">{segment.level}</span>
            </div>
            
            <div className="count-badge">
              {segment.count}
            </div>

            <div className="bar-container">
              <div className="bar-scale">
                <div className="scale-marker" style={{ left: '25%' }}></div>
                <div className="scale-marker" style={{ left: '50%' }}></div>
                <div className="scale-marker" style={{ left: '75%' }}></div>
              </div>
              <div 
                className={`risk-bar ${segment.className}`}
                style={{ width: '0%' }}
                data-width={`${(segment.count / maxCount) * 100}%`}
              >
                <div className="bar-gradient"></div>
                <div className="percentage-badge">{getPercentage(segment.count)}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {hoveredSegment && (
        <div 
          className="segment-tooltip"
          style={{ 
            left: `${tooltipPosition.x}px`, 
            top: `${tooltipPosition.y}px` 
          }}
        >
          <div className="tooltip-header">
            <span className={`tooltip-indicator ${hoveredSegment.className}`}></span>
            <span className="tooltip-title">{hoveredSegment.level} Drivers</span>
          </div>
          <div className="tooltip-content">
            <div className="tooltip-stat">
              <span className="stat-label">Count:</span>
              <span className="stat-value">{hoveredSegment.count}</span>
            </div>
            <div className="tooltip-stat">
              <span className="stat-label">Percentage:</span>
              <span className="stat-value">{getPercentage(hoveredSegment.count)}</span>
            </div>
          </div>
        </div>
      )}
      
      {!disableNavigation && (
        <div className="chart-footer">
          <div className="chart-note">Click to view detailed breakdown</div>
        </div>
      )}
    </div>
  );
};

DriversSegmentation.propTypes = {
  vehicleId: PropTypes.string,
  disableNavigation: PropTypes.bool
};

export default DriversSegmentation;